import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { usePortalSlug } from "@/contexts/PortalDomainContext";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import api from "@/lib/api";
import { loadTenantFont, resolveLogoUrl } from "@/lib/branding";
import { Button } from "@/components/ui/button";
import { Recycle, MapPin, Users, Fire, ArrowLeft, ArrowRight, HandWaving, WhatsappLogo, EnvelopeSimple, ChatCircle, LinkSimple, CheckCircle } from "@phosphor-icons/react";

export default function PortalInterestMap() {
  const slug = usePortalSlug();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [token, setToken] = useState(null);
  const [tokenMissing, setTokenMissing] = useState(false);
  const [focused, setFocused] = useState(null);
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const [copied, setCopied] = useState(false);

  // Load map data + mapbox token
  useEffect(() => {
    api.get(`/portal/${slug}/interest-map`).then((r) => {
      setData(r.data);
      const b = r.data.tenant?.branding || {};
      document.documentElement.style.setProperty("--tenant-primary", b.primary_color || "#2A86A8");
      document.documentElement.style.setProperty("--tenant-secondary", b.secondary_color || "#3FA64E");
      document.documentElement.style.setProperty("--tenant-font", `"${b.font_family || "Outfit"}", sans-serif`);
      loadTenantFont(b.font_family);
      document.title = `Interessenten-Karte — ${r.data.tenant.name}`;
    }).catch((e) => setErr(e.response?.data?.detail || "Mandant nicht gefunden"));
    api.get("/mapbox/config").then((r) => {
      if (r.data?.public_token) setToken(r.data.public_token);
      else setTokenMissing(true);
    }).catch(() => setTokenMissing(true));
  }, [slug]);

  const drawClusters = useCallback(() => {
    if (!mapRef.current || !data) return;
    markersRef.current.forEach((m) => m.remove());
    markersRef.current = [];
    const primary = data.tenant.branding?.primary_color || "#2A86A8";
    const clusters = data.clusters || [];
    clusters.forEach((c) => {
      const el = document.createElement("div");
      el.setAttribute("data-testid", `map-cluster-${c.zip || c.city}`);
      const size = Math.min(90, 40 + c.count * 6);
      const bg = c.hot ? "#F97316" : primary;
      const glow = c.hot ? "rgba(249,115,22,0.4)" : `${primary}55`;
      el.style.cssText = `width:${size}px;height:${size}px;border-radius:50%;background:${bg};border:3px solid white;box-shadow:0 6px 20px ${glow};display:flex;flex-direction:column;align-items:center;justify-content:center;color:white;font-weight:800;font-family:var(--tenant-font);cursor:pointer;transition:transform 0.15s;`;
      const countEl = document.createElement("div");
      countEl.style.cssText = `font-size:${Math.min(24, 14 + c.count / 2)}px;line-height:1;`;
      countEl.textContent = String(c.count);
      const labelEl = document.createElement("div");
      labelEl.style.cssText = "font-size:9px;letter-spacing:0.15em;text-transform:uppercase;opacity:0.85;margin-top:2px;";
      labelEl.textContent = c.hot ? "🔥 Hot" : "warten";
      el.replaceChildren(countEl, labelEl);
      el.addEventListener("mouseenter", () => { el.style.transform = "scale(1.08)"; });
      el.addEventListener("mouseleave", () => { el.style.transform = "scale(1)"; });
      el.addEventListener("click", () => setFocused(c));
      const marker = new mapboxgl.Marker(el).setLngLat([c.lng, c.lat]).addTo(mapRef.current);
      markersRef.current.push(marker);
    });
    // fit bounds if any
    if (clusters.length > 0) {
      const bounds = new mapboxgl.LngLatBounds();
      clusters.forEach((c) => bounds.extend([c.lng, c.lat]));
      mapRef.current.fitBounds(bounds, { padding: 100, maxZoom: 10, duration: 800 });
    }
  }, [data]);

  // Initialize map
  useEffect(() => {
    if (!token || !data || mapRef.current || !containerRef.current) return;
    mapboxgl.accessToken = token;
    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: "mapbox://styles/mapbox/light-v11",
      center: [10.4515, 51.1657],
      zoom: 5.5,
    });
    map.addControl(new mapboxgl.NavigationControl(), "top-right");
    map.on("load", () => {
      mapRef.current = map;
      drawClusters();
    });
    return () => { map.remove(); mapRef.current = null; markersRef.current = []; };
  }, [token, data, drawClusters]);


  if (err) return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="text-center">
        <h1 className="font-heading text-3xl font-bold mb-2">404</h1>
        <p className="text-zinc-500 mb-4">{err}</p>
        <Link to="/" className="underline text-sm">← Zur Tonneva Startseite</Link>
      </div>
    </div>
  );
  if (!data) return <div className="min-h-screen flex items-center justify-center">Lade …</div>;

  const { tenant, clusters = [], hot_threshold, min_anonymity, total_shown } = data;
  const b = tenant.branding || {};
  const primary = b.primary_color || "#2A86A8";
  const secondary = b.secondary_color || "#3FA64E";
  const isEmpty = clusters.length === 0;

  return (
    <div className="min-h-screen bg-white font-tenant text-tenant-text" style={{ fontFamily: "var(--tenant-font)" }} data-testid="portal-interest-map-page">
      {/* Header */}
      <header className="border-b border-black/5">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to={`/portal/${slug}`} className="flex items-center gap-3">
            {b.logo_url ? (
              <img src={resolveLogoUrl(b.logo_url)} alt={tenant.name} className="h-8" />
            ) : (
              <div className="w-9 h-9 rounded-md flex items-center justify-center" style={{ background: primary }}>
                <Recycle size={18} weight="bold" className="text-white" />
              </div>
            )}
            <div>
              <div className="font-bold tracking-tight">{tenant.name}</div>
              <div className="text-[10px] text-zinc-500">{tenant.city}</div>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <Link to={`/portal/${slug}`} className="hidden sm:flex text-xs font-medium px-3 py-2 rounded-md hover:bg-zinc-100 text-zinc-600">
              <ArrowLeft size={14} weight="bold" className="mr-1" /> Zurück
            </Link>
            <Link to={`/portal/${slug}/book`}>
              <Button className="rounded-md text-white" style={{ background: primary }} data-testid="map-header-book-btn">
                Adresse prüfen
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden py-14" style={{ background: `linear-gradient(135deg, ${primary}08 0%, ${secondary}08 100%)` }}>
        <div className="max-w-5xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 text-[10px] uppercase tracking-[0.2em] font-bold rounded-full mb-4" style={{ background: `${primary}15`, color: primary }}>
            <MapPin size={12} weight="fill" /> Interessenten-Karte
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-extrabold tracking-tight leading-tight">
            Wo warten Ihre Nachbarn <span style={{ color: primary }}>schon?</span>
          </h1>
          <p className="mt-4 text-zinc-600 max-w-2xl mx-auto leading-relaxed">
            Diese Karte zeigt <strong>anonymisiert</strong>, aus welchen Orten aktuell Interessenten warten. Ab <strong>{hot_threshold} Anfragen</strong> pro Ort planen wir eine Sondertour — je mehr Nachbarn sich eintragen, desto schneller kommen wir vorbei.
          </p>
          <div className="mt-6 inline-flex items-center gap-4 bg-white border border-black/10 rounded-xl px-6 py-3 shadow-sm text-sm" data-testid="map-stat-badge">
            <div className="flex items-center gap-2"><Users size={16} weight="bold" style={{ color: primary }} /> <strong>{total_shown}</strong> Interessenten</div>
            <span className="text-zinc-300">·</span>
            <div className="flex items-center gap-2"><MapPin size={16} weight="bold" style={{ color: primary }} /> <strong>{clusters.length}</strong> {clusters.length === 1 ? "Ort" : "Orte"}</div>
            <span className="text-zinc-300">·</span>
            <div className="flex items-center gap-2"><Fire size={16} weight="fill" className="text-orange-500" /> <strong>{clusters.filter((c) => c.hot).length}</strong> Hot Spots</div>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="py-12 border-t border-black/5">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-[1fr_320px] gap-6">
            {tokenMissing ? (
              <div className="border border-dashed border-black/20 bg-zinc-50 flex items-center justify-center text-center p-12 rounded-xl" style={{ height: 560 }} data-testid="map-token-missing">
                <div>
                  <div className="font-heading text-lg font-bold tracking-tight">Karte deaktiviert</div>
                  <p className="text-xs text-zinc-500 mt-2 max-w-sm">Mapbox-Token nicht konfiguriert. Cluster werden trotzdem in der Liste rechts angezeigt.</p>
                </div>
              </div>
            ) : (
              <div ref={containerRef} style={{ height: 560, width: "100%" }} className="border border-black/10 rounded-xl overflow-hidden" data-testid="map-container" />
            )}

            {/* Sidebar-Liste */}
            <aside className="bg-white border border-black/10 rounded-xl overflow-hidden" data-testid="map-cluster-list">
              <div className="px-5 py-3 border-b border-black/10 bg-zinc-50">
                <h3 className="font-heading font-semibold text-sm">Orte im Überblick</h3>
                <p className="text-[10px] text-zinc-500 mt-0.5">Sortiert nach Anzahl · min. {min_anonymity} für Anzeige</p>
              </div>
              {isEmpty ? (
                <div className="p-6 text-center text-zinc-400 text-sm">
                  <HandWaving size={26} weight="bold" className="mx-auto mb-2 text-zinc-300" />
                  Noch keine Cluster.<br/>Seien Sie Erste{tenant.city ? `r in ${tenant.city}` : ""}!
                </div>
              ) : (
                <div className="max-h-[500px] overflow-y-auto divide-y divide-black/5">
                  {clusters.map((c) => (
                    <button
                      key={`${c.city}-${c.zip}`}
                      onClick={() => { setFocused(c); if (mapRef.current) mapRef.current.flyTo({ center: [c.lng, c.lat], zoom: 11, duration: 800 }); }}
                      className={`w-full text-left p-4 hover:bg-zinc-50 transition ${focused && focused.city === c.city && focused.zip === c.zip ? "bg-zinc-50" : ""}`}
                      data-testid={`cluster-item-${c.zip || c.city}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <div className="font-semibold text-sm truncate">{c.city}</div>
                        <div className={`font-heading text-2xl font-black leading-none ${c.hot ? "text-orange-500" : ""}`} style={c.hot ? {} : { color: primary }}>{c.count}</div>
                      </div>
                      <div className="text-[10px] text-zinc-500 mb-1.5">PLZ {c.zip || "—"}</div>
                      {c.hot ? (
                        <div className="inline-flex items-center gap-1 text-[10px] font-bold text-orange-700 bg-orange-100 px-1.5 py-0.5 rounded">
                          <Fire size={10} weight="fill" /> Sondertour wird geplant
                        </div>
                      ) : (
                        <div className="text-[10px] text-zinc-600">
                          Noch <strong>{c.remaining_to_hot}</strong> {c.remaining_to_hot === 1 ? "Interessent" : "Interessenten"} bis zur Sondertour
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </aside>
          </div>
        </div>
      </section>

      {/* Focused-Detail */}
      {focused && (
        <section className="border-t border-black/5 py-14" style={{ background: focused.hot ? "linear-gradient(135deg, #F97316 0%, #EA580C 100%)" : `linear-gradient(135deg, ${primary} 0%, ${secondary} 100%)` }} data-testid="focused-detail">
          <div className="max-w-4xl mx-auto px-6 text-center text-white">
            <div className="text-xs uppercase tracking-[0.25em] font-bold opacity-80 mb-3">Ausgewählt</div>
            <h2 className="font-heading text-4xl md:text-5xl font-extrabold tracking-tight">
              {focused.city}
            </h2>
            <div className="mt-4 flex items-center justify-center gap-8 text-sm">
              <div><strong className="text-4xl font-heading font-black">{focused.count}</strong><br/><span className="opacity-80 text-xs uppercase tracking-widest">Interessenten</span></div>
              {focused.hot ? (
                <div><Fire size={30} weight="fill" className="mx-auto mb-1" /><span className="opacity-90 text-xs uppercase tracking-widest">Sondertour wird geplant</span></div>
              ) : (
                <div><strong className="text-4xl font-heading font-black">{focused.remaining_to_hot}</strong><br/><span className="opacity-80 text-xs uppercase tracking-widest">bis Sondertour</span></div>
              )}
            </div>
            <p className="mt-5 opacity-90 max-w-md mx-auto leading-relaxed">
              {focused.hot
                ? `Genug Nachbarn haben sich eingetragen — wir informieren alle in ${focused.city} zuerst, sobald Termine feststehen.`
                : `Fehlen nur noch ${focused.remaining_to_hot} Personen aus ${focused.city}, damit wir eine Sondertour einplanen. Überzeugen Sie Ihre Nachbarn oder tragen Sie sich selbst ein!`}
            </p>
            <div className="mt-8 flex flex-wrap gap-3 justify-center">
              <button
                onClick={() => navigate(`/portal/${slug}/book?prefill_city=${encodeURIComponent(focused.city)}&prefill_zip=${encodeURIComponent(focused.zip)}`)}
                className="inline-flex items-center gap-2 h-12 px-7 rounded-md bg-white text-black font-medium hover:bg-zinc-100"
                data-testid="focused-join-btn"
              >
                Auch aus {focused.city} eintragen <ArrowRight size={14} weight="bold" />
              </button>
              <button
                onClick={() => setFocused(null)}
                className="inline-flex items-center gap-2 h-12 px-7 rounded-md border border-white/40 text-white hover:bg-white/10"
                data-testid="focused-close-btn"
              >
                Andere Orte ansehen
              </button>
            </div>

            {/* Share / Nachbarn einladen */}
            <ShareSection focused={focused} tenant={tenant} slug={slug} copied={copied} setCopied={setCopied} />
          </div>
        </section>
      )}

      {/* Bottom CTA */}
      {!focused && (
        <section className="py-20 border-t border-black/5 text-center relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${primary} 0%, ${secondary} 100%)` }}>
          <div className="max-w-3xl mx-auto px-6 text-white">
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight">Ihr Ort fehlt noch?</h2>
            <p className="mt-4 opacity-90">Tragen Sie sich als Interessent ein — je mehr Nachbarn zusammenkommen, desto schneller kommen wir.</p>
            <div className="mt-8">
              <Link to={`/portal/${slug}/book`}>
                <Button className="rounded-md h-12 px-8 text-base bg-white text-black hover:bg-zinc-100" data-testid="map-cta-book-btn">
                  Adresse eintragen <ArrowRight size={14} weight="bold" className="ml-1" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      <footer className="py-10 border-t border-black/5">
        <div className="max-w-6xl mx-auto px-6 flex justify-between text-xs text-zinc-500">
          <Link to={`/portal/${slug}`} className="hover:underline">← Zur Startseite</Link>
          <div className="flex gap-4">
            <Link to={`/portal/${slug}/impressum`} className="hover:underline" data-testid="footer-impressum-link">Impressum</Link>
            <Link to={`/portal/${slug}/agb`} className="hover:underline" data-testid="footer-agb-link">AGB</Link>
            <Link to={`/portal/${slug}/datenschutz`} className="hover:underline" data-testid="footer-datenschutz-link">Datenschutz</Link>
          </div>
          <div>Powered by <span className="font-semibold">Tonneva</span></div>
        </div>
      </footer>
    </div>
  );
}


function ShareSection({ focused, tenant, slug, copied, setCopied }) {
  const shareUrl = `${window.location.origin}/portal/${slug}/karte?utm_source=share&utm_from=${encodeURIComponent(focused.city)}`;
  const remaining = focused.remaining_to_hot;
  const nachbarnText = focused.hot
    ? `In ${focused.city} haben sich schon ${focused.count} Nachbarn für saubere Mülltonnen eingetragen — ${tenant.name} plant gerade eine Sondertour. Mach mit, dann kommen wir zuerst zu euch: ${shareUrl}`
    : remaining === 1
      ? `In ${focused.city} warten schon ${focused.count} Nachbarn auf saubere Mülltonnen — es fehlt nur noch 1 Person, damit ${tenant.name} eine Sondertour einplant. Sei du diese Person: ${shareUrl}`
      : `In ${focused.city} warten schon ${focused.count} Nachbarn auf saubere Mülltonnen. Es fehlen nur noch ${remaining} Personen, damit ${tenant.name} eine Sondertour einplant. Trag dich hier ein: ${shareUrl}`;
  const emailSubject = `Saubere Mülltonnen für ${focused.city}?`;

  const openWhatsapp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(nachbarnText)}`, "_blank", "noopener");
  };
  const openMail = () => {
    window.location.href = `mailto:?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(nachbarnText)}`;
  };
  const openSms = () => {
    window.location.href = `sms:?body=${encodeURIComponent(nachbarnText)}`;
  };
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(nachbarnText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      // fallback: select the textarea (rare browsers)
      const t = document.getElementById("share-text-fallback");
      if (t) { t.select(); document.execCommand("copy"); setCopied(true); setTimeout(() => setCopied(false), 2200); }
    }
  };

  return (
    <div className="mt-14 pt-8 border-t border-white/20 text-left max-w-2xl mx-auto" data-testid="share-section">
      <div className="text-center mb-5">
        <div className="inline-flex items-center gap-2 px-3 py-1 text-[10px] uppercase tracking-[0.25em] font-bold rounded-full bg-white/15">
          <Users size={11} weight="bold" /> Nachbarn einladen
        </div>
        <h3 className="mt-3 font-heading text-2xl md:text-3xl font-bold tracking-tight">
          {focused.hot
            ? `Deine Nachbarn zuerst benachrichtigen`
            : remaining === 1
              ? `Noch 1 Nachbar fehlt — hol ihn ins Boot`
              : `Je mehr mitmachen, desto schneller kommen wir`}
        </h3>
        <p className="mt-2 text-sm opacity-85">Ein Klick, ein Nachbar mehr aus {focused.city}.</p>
      </div>

      <div className="bg-white/10 border border-white/20 rounded-xl p-4 text-sm leading-relaxed" data-testid="share-preview">
        {nachbarnText}
      </div>
      {/* Hidden textarea for copy-fallback */}
      <textarea id="share-text-fallback" defaultValue={nachbarnText} className="sr-only" readOnly />

      <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-2">
        <button
          onClick={openWhatsapp}
          className="inline-flex items-center justify-center gap-2 h-12 px-4 rounded-md bg-[#25D366] hover:bg-[#1DA851] text-white font-medium transition"
          data-testid="share-whatsapp-btn"
        >
          <WhatsappLogo size={18} weight="fill" /> WhatsApp
        </button>
        <button
          onClick={openMail}
          className="inline-flex items-center justify-center gap-2 h-12 px-4 rounded-md bg-white/15 hover:bg-white/25 text-white font-medium transition border border-white/25"
          data-testid="share-email-btn"
        >
          <EnvelopeSimple size={18} weight="bold" /> E-Mail
        </button>
        <button
          onClick={openSms}
          className="inline-flex items-center justify-center gap-2 h-12 px-4 rounded-md bg-white/15 hover:bg-white/25 text-white font-medium transition border border-white/25"
          data-testid="share-sms-btn"
        >
          <ChatCircle size={18} weight="bold" /> SMS
        </button>
        <button
          onClick={copyLink}
          className={`inline-flex items-center justify-center gap-2 h-12 px-4 rounded-md font-medium transition ${copied ? "bg-emerald-500 text-white" : "bg-white text-black hover:bg-zinc-100"}`}
          data-testid="share-copy-btn"
        >
          {copied ? <><CheckCircle size={18} weight="fill" /> Kopiert!</> : <><LinkSimple size={18} weight="bold" /> Text kopieren</>}
        </button>
      </div>
      <p className="mt-3 text-[10px] text-center opacity-70">
        Der Link enthält kein Tracking-Cookie. Wir zählen nur, welcher Kanal am besten funktioniert.
      </p>
    </div>
  );
}
